import { difference, get, map, pick } from 'lodash';
import { put, select, takeLatest, call, cancelled } from 'redux-saga/effects';
import { fetchSurveyLinks, postSurveyAnswers } from 'api/modules/survey/api';
import { REF_KEYS_LIST, dropOffKidsQId, primaryRouteObjectId, primaryRoutePath, secondaryRouteFrequencyPath, secondaryRouteObjectId, secondaryRoutePath, subQuestionIdPrefix } from 'constants/onboarding';
import { MonToFri } from 'shared/interfaces/Commute';
import { getArrayValue } from 'utils/index';
import { calculateAvailableFrequency, generateStopFromLocation, insertStopsAt } from 'utils/routes';
import { formatRouteTransitLegs } from 'utils/formatters/routes';
import { defaultSurvey, enterpriseSurvey, normalize } from 'store/schemas';
import { callApiSaga } from 'store/callApiSaga';
import { logger } from '@commutifi-fe/logger';
import { isUuid } from 'utils/helpers';
import { selectors as surveySelectors } from './index';
import types from './types';
import { fetchAccountDefaultSurvey } from 'api/modules/accounts/api';

/**
 * ---------------- ACTIONS ----------------
 */
export const upsertSurveyQuestions = questions => ({
  type: types.UPSERT_SURVEY_QUESTION,
  payload: {
    questions: getArrayValue(questions)
  }
});
export const removeSurveyQuestions = questionIds => ({
  type: types.REMOVE_SURVEY_QUESTION,
  payload: {
    questionIds: getArrayValue(questionIds)
  }
});
export const updateSurveyAnswers = (stepNumber, answers) => ({
  type: types.UPDATE_SURVEY_ANSWERS,
  payload: {
    stepNumber,
    answers
  }
});
export const resetSurveyAnswers = () => ({
  type: types.RESET_SURVEY_ANSWERS
});
export const createSurveyAnswers = (surveyId, account, isAddressUpdate, promise) => ({
  type: types.CREATE_SURVEY_ANSWERS,
  surveyId,
  account,
  isAddressUpdate,
  promise
});
export const resetSurvey = () => ({
  type: types.RESET_SURVEY
});

/**
 * This context is used to prefill the survey with the account context. It is also useful to extract
 * the account context to submit the survey
 * @param {*} accountContext - The partial account information to prefill enterprise id,
 *                             account name, email, and potentially more
 */
export const setSurveyAccountContext = accountContext => ({
  type: types.SET_SURVEY_ACCOUNT_CONTEXT_TRIGGER,
  payload: {
    accountContext
  }
});
export const loadEnterpriseSurvey = (queryParams, options, promise) => ({
  type: types.FETCH_SURVEY_TRIGGER,
  types: [types.FETCH_SURVEY_REQUEST, types.FETCH_SURVEY_BEFORE_SUCCESS, types.FETCH_SURVEY_FAILURE],
  callAPI: () => fetchSurveyLinks(queryParams),
  payload: {
    ...queryParams,
    options
  },
  promise,
  normalize: data => {
    const record = data?.records?.[0];
    if (!record) {
      throw {
        status: 404
      };
    }
    return normalize(record, enterpriseSurvey);
  }
});
export const loadAccountDefaultSurvey = (accountId, queryParams, options, promise) => ({
  type: types.FETCH_ACCOUNT_DEFAULT_SURVEY_TRIGGER,
  types: [types.FETCH_ACCOUNT_DEFAULT_SURVEY_REQUEST, types.FETCH_ACCOUNT_DEFAULT_SURVEY_BEFORE_SUCCESS, types.FETCH_ACCOUNT_DEFAULT_SURVEY_FAILURE],
  callAPI: () => fetchAccountDefaultSurvey(accountId, queryParams),
  payload: {
    ...queryParams,
    options
  },
  promise,
  normalize: data => normalize(data, defaultSurvey)
});
export const loadAccountDefaultSurveySuccess = survey => ({
  type: types.FETCH_ACCOUNT_DEFAULT_SURVEY_SUCCESS,
  response: normalize(survey, defaultSurvey)
});
export const _postSurvey = (surveyId, body, isAddressUpdate, promise) => ({
  type: types.POST_SURVEY_TRIGGER,
  types: [types.POST_SURVEY_REQUEST, types.POST_SURVEY_SUCCESS, types.POST_SURVEY_FAILURE],
  callAPI: () => postSurveyAnswers(surveyId, body, {
    allowProfileUpdate: isAddressUpdate
  }),
  promise
});

/**
 * ---------------- SAGAS ----------------
 */
export const enterpriseSurveySagas = [takeLatest(types.FETCH_SURVEY_TRIGGER, callApiSagaWithAuthCheck), takeLatest(types.FETCH_SURVEY_BEFORE_SUCCESS, resetSurveyAnswersSaga), takeLatest(types.FETCH_ACCOUNT_DEFAULT_SURVEY_BEFORE_SUCCESS, resetAccountDefaultSurveyAnswersSaga), takeLatest(types.FETCH_ACCOUNT_DEFAULT_SURVEY_TRIGGER, callApiSagaWithAuthCheck), takeLatest(types.CREATE_SURVEY_ANSWERS, processSurvey), takeLatest(types.POST_SURVEY_TRIGGER, callApiSaga), takeLatest(types.SET_SURVEY_ACCOUNT_CONTEXT_TRIGGER, setSurveyAccountContextSaga)];

/**
 * Enhanced callApiSaga that checks auth state before making API calls
 */
export function* callApiSagaWithAuthCheck(action) {
  try {
    const authState = yield select(state => state.auth);
    if (authState.loggingOut) {
      return;
    }
    const result = yield call(callApiSaga, action);
    return result;
  } catch (error) {
    if (yield cancelled()) {
      return;
    }
    throw error;
  }
}
export function* resetSurveyAnswersSaga(res) {
  yield call(resetAnswers, res);
  yield put({
    ...res,
    type: types.FETCH_SURVEY_SUCCESS
  });
  return;
}
export function* resetAccountDefaultSurveyAnswersSaga(res) {
  yield put({
    ...res,
    type: types.FETCH_ACCOUNT_DEFAULT_SURVEY_SUCCESS
  });
  const previousSurveyId = yield select(surveySelectors.getSurveyId);
  if (previousSurveyId === res.response.result.survey.id) {
    return;
  }
  return yield put(resetSurveyAnswers());
}
export function* resetAnswers(res) {
  const previousSurveyId = yield select(surveySelectors.getSurveyId);
  const {
    payload,
    response
  } = res;
  // If same survey -> no need to reset answers
  if (payload && !payload.options?.forceReset) {
    return;
  }
  if (previousSurveyId && previousSurveyId === response?.result?.survey?.id) {
    return;
  }
  return yield put(resetSurveyAnswers());
}
export function* processSurvey({
  surveyId,
  account,
  isAddressUpdate,
  promise
}) {
  try {
    const accountAnswers = account ? [{
      referenceKey: REF_KEYS_LIST.accountName,
      value: account.name
    }, {
      referenceKey: REF_KEYS_LIST.accountEmail,
      value: account.email
    }, {
      referenceKey: REF_KEYS_LIST.accountPassword,
      value: account.password
    }, {
      referenceKey: REF_KEYS_LIST.accountPreferredLocale,
      value: account.preferredLocale
    }].filter(answer => answer.value) : [];
    const idpLogin = pick(account, ['authorizationCode', 'state', 'identityProviderId', 'preferredLocale']);

    // Get survey answer
    const answers = yield select(surveySelectors.getSurveyFinalAnswers);
    const questions = yield select(surveySelectors.getQuestionsByIdOrRefKey);
    const commuteRouterQuestionId = yield select(surveySelectors.getQuestionIdForCommuteRouter);

    // Format answers
    // We filter temporary values. (used per example to build routes with activity)
    // Now that the answer formatting is done we can remove it from the
    // answers to make sure we don't send useless information to the API
    const surveyAnswers = yield map(answers, (value, key) => ({
      ...(isUuid(key) // Check if the key is a UUID which indicates it's a question ID
      ? {
        questionId: key
      } // If it's a question ID, use it directly
      : {
        referenceKey: key,
        // Otherwise use it as a reference key and look up the actual question ID
        questionId:
        // For sub-questions (conditional questions), use their parent question's ID
        // Otherwise fallback to the question's own ID, make sure the conditionalQuestionId is a valid UUID
        key === REF_KEYS_LIST.commuteProfileRoute ? commuteRouterQuestionId : ((questions[key]?.id || '').startsWith(subQuestionIdPrefix) ? questions[key]?.conditionalQuestionId && isUuid(questions[key]?.conditionalQuestionId) ? questions[key]?.conditionalQuestionId // Use parent question ID for sub-questions if it's a valid UUID
        : undefined : undefined) || (isUuid(questions[key]?.id) ? questions[key]?.id : undefined) // Use question's own ID only if it's a valid UUID
      }),
      value: _verifyAndFormatAnswer(key, value, answers)
    })).filter(answer => !(answer.questionId || '').startsWith(subQuestionIdPrefix) && !(answer.referenceKey || '').startsWith(subQuestionIdPrefix));

    // Get shortlink id to give access to some prefilled data
    const enterpriseSurveyId = yield select(surveySelectors.makeGetPrefilledData('id'));
    const surveyPayload = yield {
      enterpriseSurveyId,
      answers: [...accountAnswers, ...surveyAnswers],
      ...idpLogin
    };

    // Trigger post survey
    yield put(_postSurvey(surveyId, surveyPayload, isAddressUpdate, promise));

    // don't resolve the promise here since we pass it down to the request
    // middleware that will take care of resolving the promise if the call is successful
  } catch (error) {
    logger.errorMessage('Error while generating survey answers: ', error);
    promise && promise.reject(error);
  }
}

/**
 * HELPERS FUNCTIONS for sagas
 */

const _generateCommuteRoutes = (routesValue, allValues) => {
  // Build Primary route with the inferred frequency from the commute profile
  // working frequency, the remote frequency and the secondary commute frequency
  const primaryRouteWithoutFrequency = get(routesValue, primaryRoutePath);
  const remoteFrequency = allValues[REF_KEYS_LIST.commuteProfileRemoteFrequency];
  // Spec: "if I say remote Monday, then my CP will have Tuesday to Friday (even if not entered)"
  const commuteFrequency = allValues[REF_KEYS_LIST.commuteProfileFrequency] || {
    days: difference(MonToFri, remoteFrequency?.days || []),
    weeklyFrequency: difference(MonToFri, remoteFrequency?.days || []).length
  };
  const secondaryRouteFrequency = get(routesValue, secondaryRouteFrequencyPath, {});
  const primaryRouteFrequency = calculateAvailableFrequency(commuteFrequency, [remoteFrequency, secondaryRouteFrequency]);
  const primaryRoute = {
    ...primaryRouteWithoutFrequency,
    ...primaryRouteFrequency
  };

  // Build secondary commute route with frequency
  const secondaryRouteWithoutFrequency = get(routesValue, secondaryRoutePath);
  const secondaryRoute = secondaryRouteWithoutFrequency ? {
    ...secondaryRouteWithoutFrequency,
    weeklyFrequency: secondaryRouteFrequency.weeklyFrequency,
    ...(secondaryRouteFrequency.days && {
      days: secondaryRouteFrequency.days
    })
  } : undefined;

  // Format the information we will need for the next steps for easy access.
  // The activity mapper value looks like:
  // { routes: ['primary' | 'secondary'], frequency: Frequency, stopLocations: AddressPoint[], ['primary' | 'secondary']: {legIndex: X} }
  const activityRouteMapping = {
    [primaryRouteObjectId]: {
      route: primaryRoute,
      frequency: primaryRouteFrequency
    },
    [secondaryRouteObjectId]: {
      route: secondaryRoute,
      frequency: secondaryRouteFrequency
    }
  };
  const activityMapperValue = allValues[dropOffKidsQId];
  // If we have a value defined but no targeted routes it means the user has only one route,
  // which is the primary route
  const routesTargetedByActivities = activityMapperValue ? activityMapperValue.routes ? activityMapperValue.routes : [primaryRouteObjectId] : [];
  const activityMapperDefinedValue = activityMapperValue || {};
  let activityFrequency = activityMapperDefinedValue.frequency;
  const stops = (activityMapperDefinedValue.stopLocations || []).map(location => generateStopFromLocation(location));
  const routes = [];
  routesTargetedByActivities.forEach(routeTargetedByActivities => {
    const target = activityRouteMapping[routeTargetedByActivities];
    const targetRoute = target.route;
    const targetFrequency = target.frequency;

    // If the activity frequency is higher than the target route frequency we assume
    // an activity frequency that is the same as the target route
    if (activityFrequency.weeklyFrequency > targetFrequency.weeklyFrequency) {
      activityFrequency = targetFrequency;
    }

    // The index of the leg needs to be a number so we make sure of that.
    // And, if no leg is specified it means we target the only leg of the route at index 0
    const legIndex = +(activityMapperDefinedValue[routeTargetedByActivities] || {}).legIndex || 0;

    // When the frequency of the activity is not the same as the original route we create 2 routes:
    // 1. The original route with a new frequency which is the diff between
    //    the route frequency and the activity frequency
    // 2. A route based on the original one that contains the stops
    const routeNewFrequency = activityFrequency && targetRoute.weeklyFrequency !== activityFrequency.weeklyFrequency ? calculateAvailableFrequency(targetFrequency, activityFrequency) : undefined;
    if (routeNewFrequency) {
      // Copy route and change original frequency
      routes.push({
        ...targetRoute,
        ...routeNewFrequency,
        // The highest frequency is the primary route
        isPrimary: routeTargetedByActivities === primaryRouteObjectId && routeNewFrequency.weeklyFrequency > activityFrequency.weeklyFrequency
      });
    }

    // In all cases we keep the original route with the added stops
    routes.push({
      ...targetRoute,
      legs: insertStopsAt(targetRoute.legs, stops || [], legIndex),
      // Handles 2 cases:
      // 1. activityFrequency is not defined so we keep the original route frequency
      // 2. activityFrequency = original route frequency so we don't really care which one we keep
      weeklyFrequency: activityFrequency ? activityFrequency.weeklyFrequency : targetRoute.weeklyFrequency,
      days: activityFrequency ? activityFrequency.days : targetRoute.days,
      // If we didn't generate a new route the original route is the primary
      isPrimary: routeTargetedByActivities === primaryRouteObjectId && (!routeNewFrequency || routeNewFrequency && routeNewFrequency.weeklyFrequency < activityFrequency.weeklyFrequency)
    });
  });

  // Handle routes not affected by activities
  const remainingRouteIds = difference([primaryRouteObjectId, secondaryRouteObjectId], routesTargetedByActivities);
  remainingRouteIds.forEach(routeId => {
    const route = activityRouteMapping[routeId].route;
    route && routes.push({
      ...route,
      isPrimary: routeId === primaryRouteObjectId
    });
  });
  return routes.map(formatRouteTransitLegs);
};

/**
 * Saga to handle setting survey account context with address geocoding.
 * If the accountContext contains an address without addressComponents,
 * it will geocode the address to ensure complete data before storing.
 */
export function* setSurveyAccountContextSaga(action) {
  try {
    const {
      accountContext
    } = action.payload;

    // If there's an address field but no addressComponents, geocode it
    if (accountContext?.address && accountContext?.location && !accountContext?.addressComponents) {
      const {
        ensureAddressComponents
      } = yield import('api/thirdParty/google');
      const addressPoint = {
        formattedAddress: accountContext.address,
        location: accountContext.location,
        addressComponents: accountContext.addressComponents
      };
      const enrichedAddress = yield call(ensureAddressComponents, addressPoint);
      if (enrichedAddress) {
        // Update accountContext with enriched address data
        const enrichedAccountContext = {
          ...accountContext,
          addressComponents: enrichedAddress.addressComponents,
          location: enrichedAddress.location
        };
        yield put({
          type: types.SET_SURVEY_ACCOUNT_CONTEXT,
          payload: {
            accountContext: enrichedAccountContext
          }
        });
        return;
      }
    }

    // If no geocoding needed or geocoding failed, use original data
    yield put({
      type: types.SET_SURVEY_ACCOUNT_CONTEXT,
      payload: {
        accountContext
      }
    });
  } catch (error) {
    logger.error('Failed to set survey account context', {
      error
    });
    // On error, still set the context with original data
    yield put({
      type: types.SET_SURVEY_ACCOUNT_CONTEXT,
      payload: {
        accountContext: action.payload.accountContext
      }
    });
  }
}
const _verifyAndFormatAnswer = (questionId, value, allValues) => {
  if (questionId === REF_KEYS_LIST.commuteProfileRoute) {
    return _generateCommuteRoutes(value, allValues);
  } else if (questionId === REF_KEYS_LIST.accountBuildingId) {
    // Handle building case.
    // For validation purposes we need some information about the address location:
    // { value: string, addressComponents: object[], formattedAddress: string, location: Location }
    // The api request expects a building id only so we select only the value of the address object
    return (value || {}).value;
  } else if (questionId === REF_KEYS_LIST.accountEnterpriseId) {
    return (value || {}).key;
  } else if (questionId === REF_KEYS_LIST.commuteProfileVehicleId) {
    const vehicle = value || {};
    return vehicle.modelId || vehicle.genericModelId;
  } else if (questionId === REF_KEYS_LIST.commuteCostCarpoolDriver) {
    return {
      ...value,
      cost: `${-value.cost}`
    };
  } else {
    return value;
  }
};